"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useConfirm } from "@/components/ConfirmDialog";
import { requestJson } from "@/lib/clientRequest";
import { APP_HOME } from "@/lib/appHome";

/** Self-contained so Settings works without ever mounting Configuration. */
export default function WorkerDeleteSection({ workerId, workerName, draftScope, variant = "section" }: {
  workerId: string;
  workerName: string;
  draftScope?: string;
  variant?: "section" | "menu";
}) {
  const router = useRouter();
  const ask = useConfirm();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(false);
  const inFlight = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  async function remove() {
    if (inFlight.current) return;
    inFlight.current = true;
    setDeleting(true);
    setError(null);
    try {
      if (!(await ask({ title: `Delete "${workerName}"?`, message: "The worker, its runs, its history, and its dedicated VM and storage will be permanently deleted, including all files on that VM. Connected systems and shared resources are kept. This cannot be undone.", confirmLabel: "Delete worker" })) || !mounted.current) return;
      const url = `/api/workers/${encodeURIComponent(workerId)}`;
      let complete = false;
      for (let attempt = 0; attempt < 90 && mounted.current; attempt++) {
        const result = await requestJson<{ ok?: boolean; pending?: boolean }>(url, { method: "DELETE", fallback: "Could not delete worker. Try again." });
        if (result?.ok === true && !result.pending) { complete = true; break; }
        if (result?.pending !== true) throw new Error("Deletion was not confirmed. Try again.");
        if (attempt < 89 && mounted.current) await new Promise(resolve => setTimeout(resolve, 2000));
      }
      if (!mounted.current) return;
      if (!complete) throw new Error("VM cleanup is still pending. Try Delete worker again to finish removing its VM and storage.");
      try {
        if (draftScope) sessionStorage.removeItem(`caw:draft:${draftScope}:${workerId}`);
        else {
          for (const key of Object.keys(sessionStorage)) {
            if (key.startsWith("caw:draft:") && key.endsWith(`:${workerId}`)) sessionStorage.removeItem(key);
          }
        }
      } catch { /* Storage is optional. */ }
      window.dispatchEvent(new CustomEvent("caw:activity-refresh", { detail: { workerId } }));
      router.push(APP_HOME);
      router.refresh();
    } catch (cause) {
      if (mounted.current) setError(cause instanceof Error ? cause.message : "Could not delete worker. Try again.");
    } finally {
      inFlight.current = false;
      if (mounted.current) setDeleting(false);
    }
  }

  const action = <>
    {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
    <button type="button" onClick={() => void remove()} disabled={deleting} className="rounded-lg border border-red-500/30 px-3 py-2 text-sm text-red-400 transition hover:bg-red-500/10 disabled:opacity-50">
      {deleting ? "Deleting…" : "Delete worker"}
    </button>
  </>;
  if (variant === "menu") return <div className="space-y-2 border-t border-zinc-800 p-2">{action}</div>;
  return <section aria-labelledby="worker-delete-heading" className="space-y-3 border-t border-zinc-800 pt-6">
    <h2 id="worker-delete-heading" className="text-sm font-medium text-zinc-300">Delete worker</h2>
    <p className="text-sm text-zinc-400">Permanently remove this worker, its history, and its dedicated VM and storage.</p>
    {action}
  </section>;
}
